import Fuse from 'fuse.js';
import { CachedSearchedMarketsType } from 'src/interfaces/idashboard';

class MarketSearch {
  private markets: CachedSearchedMarketsType[];
  private fuse: Fuse<CachedSearchedMarketsType>;
  private userPrefCountryCode: string;
  private searchInput: string = '';

  constructor({
    markets,
    userPrefCountryCode,
  }: {
    markets: CachedSearchedMarketsType[];
    userPrefCountryCode: string;
  }) {
    this.markets = markets;
    this.userPrefCountryCode = userPrefCountryCode;
    console.log('user pref country code', userPrefCountryCode);

    // Create a Fuse instance with our custom sort function.
    this.fuse = new Fuse(this.markets, {
      isCaseSensitive: false,
      ignoreDiacritics: true,
      includeMatches: true,
      useExtendedSearch: true,
      includeScore: true,
      shouldSort: true,
      findAllMatches: true,
      keys: [
        { name: 'district', weight: 10 },
        { name: 'city', weight: 0.5 },
        { name: 'country', weight: 0.5 },
      ],
    });
  }

  compareResults(a: CachedSearchedMarketsType, b: CachedSearchedMarketsType): number {
    // 1. Prioritise earlier matches in the district name
    const normalizeDistrict = (district: string) =>
      district
        .split(',')[0]
        .trim()
        .toLowerCase();

    const normA = normalizeDistrict(a.district);
    const normB = normalizeDistrict(b.district);

    const indexA = normA.indexOf(this.searchInput);
    const indexB = normB.indexOf(this.searchInput);

    const effectiveA = indexA === -1 ? Number.MAX_SAFE_INTEGER : indexA;
    const effectiveB = indexB === -1 ? Number.MAX_SAFE_INTEGER : indexB;
    if (effectiveA !== effectiveB) {
      return effectiveA - effectiveB;
    }

    // 2. Prioritize markets from the user's preference country
    const aIsUserCountry = a.countryCode === this.userPrefCountryCode;
    const bIsUserCountry = b.countryCode === this.userPrefCountryCode;
    if (aIsUserCountry !== bIsUserCountry) {
      return aIsUserCountry ? -1 : 1;
    }

    // 3. Prioritize those with isPSEO true
    if (a.isPSEO !== b.isPSEO) {
      return a.isPSEO ? -1 : 1;
    }

    // 4. Prioritize by higher totalSupply (default null/undefined to 0)
    const supplyA = a.totalSupply || 0;
    const supplyB = b.totalSupply || 0;

    if (supplyA !== supplyB) {
      return supplyB - supplyA;
    }

    // 5. (Optional) Prioritize isVerified
    if (a.isVerified !== b.isVerified) {
      return a.isVerified ? -1 : 1;
    }

    // 6. Fallback: compare lexically if the indices are equal.
    return normA.localeCompare(normB);
  }

  calculate(input: string): CachedSearchedMarketsType[] {
    if (!input || input.trim() === '') {
      return [];
    }

    this.searchInput = input.toLowerCase();

    const fuseResults = this.fuse.search(input);
    const results = fuseResults.map(result => ({
      ...result.item,
      matches: result.matches,
    }));

    // Manually sort the results using the custom comparator.
    results.sort(this.compareResults.bind(this));

    console.log('DEBG: search test', results.slice(0, 50));

    // Return the top N results (for example, top 7)
    return results.slice(0, 7);
  }
}

export default MarketSearch;
